import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ChartBar, ArrowUp, ArrowDown, Lightning, CircleNotch, Globe, TrendUp, CurrencyDollar, Target
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('nx_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function BenchmarkPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [region, setRegion] = useState('SA');
  const [benchmark, setBenchmark] = useState(null);
  const [allBenchmarks, setAllBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    clientsAPI.list().then(r => {
      setClients(r.data);
      if (r.data.length > 0) setSelectedClient(r.data[0].id);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    axios.get(`${API_BASE}/benchmark/gcc/${selectedClient}?region=${region}`, authHeader()).then(r => setBenchmark(r.data)).catch(() => {});
    axios.get(`${API_BASE}/benchmark/gcc/all?region=${region}`, authHeader()).then(r => setAllBenchmarks(r.data)).catch(() => {});
  }, [selectedClient, region]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API_BASE}/benchmark/gcc/refresh/${selectedClient}?region=${region}`, {}, authHeader());
      toast.success('Benchmark enriched with live Tavily data');
      const b = await axios.get(`${API_BASE}/benchmark/gcc/${selectedClient}?region=${region}`, authHeader());
      setBenchmark(b.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Refresh failed'); }
    finally { setRefreshing(false); }
  };

  const regionLabel = region === 'SA' ? (isRTL ? 'المملكة العربية السعودية' : 'Saudi Arabia') : (isRTL ? 'الإمارات' : 'UAE');

  const radarData = benchmark ? [
    { metric: 'ROAS', client: (benchmark.comparisons?.roas?.client || 0) * 10, benchmark: (benchmark.benchmarks?.avg_roas || 0) * 10 },
    { metric: 'SEO', client: benchmark.comparisons?.seo_score?.client || 0, benchmark: benchmark.benchmarks?.avg_seo_score || 0 },
    { metric: 'CTR', client: (benchmark.benchmarks?.avg_ctr || 0) * 15, benchmark: (benchmark.benchmarks?.avg_ctr || 0) * 15 },
    { metric: 'Growth', client: benchmark.benchmarks?.organic_growth || 0, benchmark: benchmark.benchmarks?.organic_growth || 0 },
  ] : [];

  const comparisonChart = allBenchmarks?.clients?.map(c => ({
    name: c.client_name?.substring(0, 12),
    client_roas: c.client_roas,
    benchmark_roas: c.benchmark_roas,
    client_seo: c.client_seo,
    benchmark_seo: c.benchmark_seo,
  })) || [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="benchmark-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" style={{ fontFamily: isRTL ? 'Cairo' : 'Chivo' }}>
              {isRTL ? 'مقارنة سوق الخليج' : 'GCC Market Benchmark'}
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {isRTL ? 'قارن أداء عملائك مع متوسطات الصناعة في السعودية والإمارات' : 'Compare client performance vs SA/UAE industry averages'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="benchmark-region-select" className="w-36 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                <SelectItem value="SA" className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{isRTL ? 'السعودية' : 'Saudi Arabia'}</SelectItem>
                <SelectItem value="UAE" className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{isRTL ? 'الإمارات' : 'UAE'}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger data-testid="benchmark-client-select" className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="refresh-benchmark-btn" onClick={handleRefresh} disabled={refreshing}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white disabled:opacity-50">
              {refreshing ? <CircleNotch size={16} className="animate-spin mr-2" /> : <Lightning size={16} className="mr-2" />}
              {isRTL ? 'تحديث بـ Tavily' : 'Enrich with Tavily'}
            </Button>
          </div>
        </div>

        {benchmark && (
          <>
            {/* Market Insight */}
            <div className="nx-metric-card rounded-lg border-[var(--accent-border)]" data-testid="market-insight">
              <div className="flex items-center gap-2 mb-2">
                <Globe size={16} className="text-[var(--accent)]" weight="duotone" />
                <span className="text-xs text-[var(--accent)] font-mono uppercase tracking-wider">{regionLabel} Market</span>
              </div>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{benchmark.market_insight}</p>
            </div>

            {/* Comparison Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: isRTL ? 'ROAS العميل' : 'Client ROAS', value: `${benchmark.comparisons?.roas?.client || 0}x`, benchmark: `${benchmark.benchmarks?.avg_roas}x`, diff: benchmark.comparisons?.roas?.diff, color: 'var(--accent)' },
                { label: isRTL ? 'نقاط SEO' : 'SEO Score', value: benchmark.comparisons?.seo_score?.client || 0, benchmark: benchmark.benchmarks?.avg_seo_score, diff: benchmark.comparisons?.seo_score?.diff, color: 'var(--success)' },
                { label: isRTL ? 'متوسط CPC' : 'Avg CPC', value: `$${benchmark.benchmarks?.avg_cpc}`, benchmark: null, diff: null, color: 'var(--warning)' },
                { label: isRTL ? 'حجم السوق' : 'Market Size', value: `$${benchmark.benchmarks?.market_size_b}B`, benchmark: null, diff: null, color: 'var(--accent)' },
              ].map(m => (
                <div key={m.label} className="nx-metric-card rounded-lg" data-testid={`benchmark-metric-${m.label}`}>
                  <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">{m.label}</p>
                  <p className="text-2xl font-bold text-[var(--text-primary)] font-mono">{m.value}</p>
                  {m.benchmark !== null && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-[var(--text-tertiary)] font-mono">vs {m.benchmark}</span>
                      {m.diff !== null && (
                        <span className={`flex items-center gap-0.5 text-xs font-mono ${m.diff > 0 ? 'text-[var(--success)]' : m.diff < 0 ? 'text-[var(--error)]' : 'text-[var(--text-tertiary)]'}`}>
                          {m.diff > 0 ? <ArrowUp size={10} /> : m.diff < 0 ? <ArrowDown size={10} /> : null}
                          {m.diff > 0 ? '+' : ''}{m.diff}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="nx-metric-card rounded-lg" data-testid="radar-benchmark">
                <h3 className="text-sm font-bold text-[var(--text-primary)] mb-4" style={{ fontFamily: isRTL ? 'Cairo' : 'Chivo' }}>
                  {isRTL ? 'مقارنة الأداء' : 'Performance vs Benchmark'}
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="var(--border)" />
                      <PolarAngleAxis dataKey="metric" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                      <PolarRadiusAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }} />
                      <Radar name="Client" dataKey="client" stroke="var(--accent)" fill="var(--accent)" fillOpacity={0.2} />
                      <Radar name="Benchmark" dataKey="benchmark" stroke="var(--warning)" fill="var(--warning)" fillOpacity={0.1} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="nx-metric-card rounded-lg" data-testid="roas-comparison-chart">
                <h3 className="text-sm font-bold text-[var(--text-primary)] mb-4" style={{ fontFamily: isRTL ? 'Cairo' : 'Chivo' }}>
                  {isRTL ? 'مقارنة ROAS لجميع العملاء' : 'All Clients ROAS vs Benchmark'}
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={comparisonChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} />
                      <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }} axisLine={false} />
                      <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }} />
                      <Bar dataKey="client_roas" name="Client" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="benchmark_roas" name="Benchmark" fill="var(--warning)" radius={[4, 4, 0, 0]} />
                      <Legend />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Tavily Enrichment */}
            {benchmark.enrichment && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="nx-metric-card rounded-lg border-[var(--success-border)]" data-testid="market-enrichment">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendUp size={16} className="text-[var(--success)]" weight="fill" />
                    <span className="text-xs text-[var(--success)] font-mono uppercase tracking-wider">Live Market Analysis</span>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{benchmark.enrichment.market_analysis}</p>
                  {benchmark.enrichment.market_sources?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {benchmark.enrichment.market_sources.map((s, i) => (
                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                          className="text-[10px] text-[var(--text-tertiary)] font-mono hover:text-[var(--accent)] px-2 py-1 rounded bg-[var(--surface-sunken)] transition-colors duration-200">
                          {s.title?.substring(0, 35) || 'Source'}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="nx-metric-card rounded-lg border-[var(--accent-border)]" data-testid="competitor-enrichment">
                  <div className="flex items-center gap-2 mb-3">
                    <Target size={16} className="text-[var(--accent)]" weight="fill" />
                    <span className="text-xs text-[var(--accent)] font-mono uppercase tracking-wider">Competitor Landscape</span>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{benchmark.enrichment.competitor_analysis}</p>
                  {benchmark.enrichment.competitor_sources?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {benchmark.enrichment.competitor_sources.map((s, i) => (
                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                          className="text-[10px] text-[var(--text-tertiary)] font-mono hover:text-[var(--accent)] px-2 py-1 rounded bg-[var(--surface-sunken)] transition-colors duration-200">
                          {s.title?.substring(0, 35) || 'Source'}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* All Clients Table */}
            {allBenchmarks?.clients?.length > 0 && (
              <div className="nx-metric-card rounded-lg" data-testid="all-clients-benchmark">
                <h3 className="text-sm font-bold text-[var(--text-primary)] mb-4" style={{ fontFamily: isRTL ? 'Cairo' : 'Chivo' }}>
                  {isRTL ? 'جميع العملاء مقابل معايير' : 'All Clients vs'} {regionLabel} {isRTL ? '' : 'Benchmarks'}
                </h3>
                <table className="nx-table">
                  <thead>
                    <tr>
                      <th>{isRTL ? 'العميل' : 'Client'}</th>
                      <th>{isRTL ? 'القطاع' : 'Industry'}</th>
                      <th>ROAS</th>
                      <th>{isRTL ? 'المعيار' : 'Benchmark'}</th>
                      <th>{isRTL ? 'الفرق' : 'Diff'}</th>
                      <th>SEO</th>
                      <th>{isRTL ? 'المعيار' : 'Benchmark'}</th>
                      <th>{isRTL ? 'الفرق' : 'Diff'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBenchmarks.clients.map(c => (
                      <tr key={c.client_id}>
                        <td className="!text-[var(--text-primary)] font-medium">{c.client_name}</td>
                        <td><span className="nx-tag nx-tag-blue">{c.industry}</span></td>
                        <td className="font-mono">{c.client_roas}x</td>
                        <td className="font-mono text-[var(--text-tertiary)]">{c.benchmark_roas}x</td>
                        <td>
                          <span className={`flex items-center gap-0.5 font-mono text-sm ${c.roas_diff > 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                            {c.roas_diff > 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                            {c.roas_diff > 0 ? '+' : ''}{c.roas_diff}
                          </span>
                        </td>
                        <td className="font-mono">{c.client_seo}</td>
                        <td className="font-mono text-[var(--text-tertiary)]">{c.benchmark_seo}</td>
                        <td>
                          <span className={`flex items-center gap-0.5 font-mono text-sm ${c.seo_diff > 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                            {c.seo_diff > 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                            {c.seo_diff > 0 ? '+' : ''}{c.seo_diff}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
